// Raster sources for <image href>: application logos, OS badges and effect
// sprites. Each is fetched once, drawn into the shared atlas at a fixed size and
// then referenced by every node that uses it.
//
// The atlas is a canvas the GL context reads back, so a source that cannot be
// fetched with CORS would taint it and break every subsequent upload. Such
// sources are dropped and reported once rather than risking that; the map draws
// without the image, which is what SVG does with a broken href anyway.

const RASTER_SIZE = 128;

const STATE_LOADING = 0;
const STATE_READY = 1;
const STATE_FAILED = 2;

export class ImageStore {
  constructor(atlas, onLoaded) {
    this.atlas = atlas;
    this.onLoaded = onLoaded;
    this.entries = new Map();
    this.warned = false;
  }

  // Returns an entry immediately; `state` becomes READY once the raster lands in
  // the atlas, at which point `onLoaded` has already run so batches referencing
  // it can be rebuilt.
  get(href) {
    if (!href) return null;

    let entry = this.entries.get(href);
    if (entry) return entry;

    entry = { state: STATE_LOADING, slot: null, aspect: 1 };
    this.entries.set(href, entry);
    this.load(href, entry);
    return entry;
  }

  load(href, entry) {
    const image = new Image();

    // Same-origin assets do not need this, but setting it unconditionally means
    // a cross-origin source either arrives usable or fails cleanly.
    image.crossOrigin = "anonymous";

    image.onload = () => {
      try {
        this.rasterise(image, entry);
      } catch (e) {
        entry.state = STATE_FAILED;
        this.warnOnce(href, e);
        return;
      }
      entry.state = STATE_READY;
      if (this.onLoaded) this.onLoaded(href);
    };

    image.onerror = () => {
      entry.state = STATE_FAILED;
      this.warnOnce(href, null);
    };

    image.src = href;
  }

  rasterise(image, entry) {
    const naturalWidth = image.naturalWidth || image.width || RASTER_SIZE;
    const naturalHeight = image.naturalHeight || image.height || RASTER_SIZE;

    const scale = RASTER_SIZE / Math.max(naturalWidth, naturalHeight);
    const width = Math.max(1, Math.round(naturalWidth * scale));
    const height = Math.max(1, Math.round(naturalHeight * scale));

    const slot = this.atlas.allocate(width, height);
    if (!slot) {
      entry.state = STATE_FAILED;
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(image, 0, 0, width, height);

    // getImageData throws on a tainted canvas, which is exactly the case that
    // must not reach the atlas upload.
    const data = ctx.getImageData(0, 0, width, height);

    this.atlas.upload(slot, data);

    entry.slot = slot;
    entry.aspect = naturalWidth / naturalHeight;
  }

  warnOnce(href, error) {
    if (this.warned) return;
    this.warned = true;
    console.warn(
      "[borg-map] image could not be loaded for the WebGL renderer (needs CORS): " +
        href,
      error || ""
    );
  }
}

export { STATE_LOADING, STATE_READY, STATE_FAILED };
